<div class="page-container min-h-0 flex-1 gap-6 overflow-hidden">
  <app-page-header
    title="New collection"
    description="Create a collection and define its columns. You can use this data in your forms."
    backLabel="Data Library"
    backLink="/data-library">
    <div class="flex flex-wrap gap-2">
      <lib-signal-button
        class="collection-header-outline-btn"
        appearance="outline"
        iconSrc="assets/icons/components/file-uploader.svg"
        [fullWidth]="false"
        (clicked)="onImportCsv()">
        Import CSV
      </lib-signal-button>
      <lib-signal-button
        class="collection-header-outline-btn"
        appearance="outline"
        [fullWidth]="false"
        (clicked)="cancel()">
        Cancel
      </lib-signal-button>
      <lib-signal-button
        iconSrc="assets/icons/content-save.svg"
        [fullWidth]="false"
        [disabled]="createState.loading()"
        (clicked)="createCollection()">
        Create collection
      </lib-signal-button>
    </div>
  </app-page-header>

  <section
    class="page-fill min-h-0 overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm">
      <div
        class="grid h-full min-h-0 min-w-0 grid-cols-1 overflow-hidden lg:grid-cols-[minmax(0,1fr)_280px]">
        <div class="flex h-full min-h-0 min-w-0 flex-col gap-6 overflow-hidden p-5 sm:p-6 lg:p-8">
          <div class="flex shrink-0 gap-3">
            <div
              class="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-600">
              <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <ellipse cx="12" cy="5" rx="7" ry="2.5" stroke="currentColor" stroke-width="1.6" />
                <path
                  d="M5 5v6c0 1.4 3.1 2.5 7 2.5s7-1.1 7-2.5V5"
                  stroke="currentColor"
                  stroke-width="1.6" />
                <path
                  d="M5 11v6c0 1.4 3.1 2.5 7 2.5s7-1.1 7-2.5v-6"
                  stroke="currentColor"
                  stroke-width="1.6" />
              </svg>
            </div>
            <div class="min-w-0 flex-1">
              <lib-signal-text-field
                label="Collection name"
                placeholder="Customers"
                [value]="name()"
                [debounceValueChange]="false"
                (valueChange)="name.set($event)" />
              <p class="mt-1.5 text-xs leading-5 text-neutral-500">
                Give a clear name for this collection. You can add, remove or reorder columns below.
              </p>
            </div>
          </div>

          <div class="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden">
            <div class="flex shrink-0 flex-wrap items-start justify-between gap-3">
              <div>
                <h2 class="text-sm font-semibold text-neutral-900">Columns</h2>
                <p class="mt-0.5 text-xs text-neutral-500">
                  Define the fields for this collection.
                </p>
              </div>
              <button
                type="button"
                class="inline-flex cursor-pointer items-center gap-1.5 rounded-lg bg-primary-50 px-3 py-1.5 text-sm font-medium text-primary-700 transition hover:bg-primary-100"
                (click)="addColumn()">
                <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M12 5v14M5 12h14"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linecap="round" />
                </svg>
                Add column
              </button>
            </div>

            <div
              class="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain pr-1"
              cdkDropList
              (cdkDropListDropped)="onColumnDrop($event)">
              @for (column of columns(); track column.id) {
                <div
                  class="shrink-0 rounded-xl border border-neutral-200 bg-white p-3 shadow-sm sm:p-4"
                  [attr.data-column-id]="column.id"
                  cdkDrag
                  cdkDragPreviewClass="collection-column-drag-preview">
                  <div class="flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      cdkDragHandle
                      class="flex h-10 w-10 shrink-0 cursor-grab items-center justify-center rounded-lg text-neutral-400 transition hover:bg-neutral-50 hover:text-neutral-700 active:cursor-grabbing"
                      aria-label="Reorder column">
                      <app-svg-icon src="assets/icons/drag-vertical.svg" [size]="24" />
                    </button>

                    <div
                      [class]="
                        'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ring-1 ring-black/5 ' +
                        typeChipClass(column.valueType)
                      ">
                      <app-svg-icon
                        [src]="iconSrcForType(column.valueType)"
                        [size]="18" />
                    </div>

                    <div class="w-52 shrink-0 sm:w-64">
                      <lib-signal-text-field
                        label="Label"
                        appearance="ifta"
                        placeholder="e.g. Customer Id"
                        [value]="column.label"
                        [debounceValueChange]="false"
                        (valueChange)="setLabel(column.id, $event)" />
                    </div>

                    <div class="w-36 shrink-0 sm:w-40">
                      <lib-signal-dropdown
                        label="Type"
                        appearance="ifta"
                        [items]="valueTypeOptions"
                        valuePath="value"
                        displayPath="label"
                        [value]="column.valueType"
                        [debounceValueChange]="false"
                        (valueChange)="setValueType(column.id, $event)" />
                    </div>

                    <lib-signal-radio
                      label="Unique"
                      groupName="collection-unique-column"
                      [optionValue]="column.id"
                      [value]="uniqueColumnId()"
                      (valueChange)="setUnique($event)" />

                    <lib-signal-checkbox
                      label="Required"
                      [value]="column.required"
                      (valueChange)="setRequired(column.id, $event)" />

                    <button
                      type="button"
                      class="ml-auto inline-flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-neutral-200 text-red-600 transition hover:border-red-200 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-neutral-200 disabled:hover:bg-transparent"
                      aria-label="Delete column"
                      [disabled]="columns().length <= 1"
                      (click)="removeColumn(column.id)">
                      <app-svg-icon src="assets/icons/trash-can-outline.svg" [size]="18" />
                    </button>
                  </div>

                  <div
                    *cdkDragPlaceholder
                    class="h-20 rounded-xl border border-dashed border-neutral-300 bg-neutral-100/80"></div>
                </div>
              }
            </div>
          </div>
        </div>

        <aside
          class="collection-tip-sidebar flex h-full min-h-0 min-w-0 flex-col overflow-y-auto border-t border-neutral-200 p-6 lg:border-l lg:border-t-0">
        <div class="collection-tip-illustration mx-auto mb-5 w-full max-w-[220px]">
          <img
            src="assets/images/data-library/collection-tip.png"
            alt=""
            class="h-auto w-full object-contain"
            width="220"
            height="220" />
        </div>

        <h3 class="text-base font-semibold text-neutral-950">
          Build a reusable collection
        </h3>
        <p class="mt-2 text-sm leading-6 text-neutral-600">
          Create a collection of data that can be used across multiple forms, such as customers,
          products, employees and more.
        </p>

        <ul class="mt-5 flex flex-col gap-4">
          <li class="flex gap-3">
            <span
              class="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white text-primary-600 shadow-sm">
              <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M10 14l4-4M8 16H6a3 3 0 0 1 0-6h2M16 8h2a3 3 0 1 1 0 6h-2"
                  stroke="currentColor"
                  stroke-width="1.6"
                  stroke-linecap="round" />
              </svg>
            </span>
            <div>
              <p class="text-sm font-semibold text-neutral-900">Use in forms</p>
              <p class="mt-0.5 text-xs leading-5 text-neutral-500">
                Link this data to dropdowns, autocomplete and other fields.
              </p>
            </div>
          </li>
          <li class="flex gap-3">
            <span
              class="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white text-primary-600 shadow-sm">
              <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V9l-5-6Z"
                  stroke="currentColor"
                  stroke-width="1.6"
                  stroke-linejoin="round" />
                <path
                  d="M14 3v6h6M9 13h6M9 17h4"
                  stroke="currentColor"
                  stroke-width="1.6"
                  stroke-linecap="round"
                  stroke-linejoin="round" />
              </svg>
            </span>
            <div>
              <p class="text-sm font-semibold text-neutral-900">Import data</p>
              <p class="mt-0.5 text-xs leading-5 text-neutral-500">
                Add data manually or import from Excel/CSV later.
              </p>
            </div>
          </li>
          <li class="flex gap-3">
            <span
              class="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white text-primary-600 shadow-sm">
              <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M4 7h10M4 12h16M4 17h8"
                  stroke="currentColor"
                  stroke-width="1.6"
                  stroke-linecap="round" />
                <circle cx="17" cy="7" r="2" stroke="currentColor" stroke-width="1.6" />
                <circle cx="11" cy="17" r="2" stroke="currentColor" stroke-width="1.6" />
              </svg>
            </span>
            <div>
              <p class="text-sm font-semibold text-neutral-900">Flexible field types</p>
              <p class="mt-0.5 text-xs leading-5 text-neutral-500">
                Text, number, date, boolean and more.
              </p>
            </div>
          </li>
        </ul>
      </aside>
      </div>
    </section>
</div>
